import { useCallback, useEffect, useRef } from 'react';
import { captureExceptionWithScope } from '@rsos/utils/sentry';

// Only the reconnect surface of StreamWS is needed here.
export interface ReconnectTaskStreamWS {
  addOnReconnectCallback: (callbacks: { [key: string]: () => void }) => void;
  removeOnReconnectCallback: (callbackName: string) => void;
  isConnected: () => boolean;
}

export interface StreamWsReconnectTaskArgs {
  streamWS: ReconnectTaskStreamWS | null | undefined;
  enabled: boolean;
  // Rejects when the task fails; the last rejection is what Sentry receives.
  run: () => Promise<unknown>;
  // Must be unique per app so two registrations on one socket don't collide.
  callbackName: string;
  // Wait this long after a reconnect, then confirm the socket is still
  // connected, so a flapping connection doesn't fire a burst of requests.
  settleMs?: number;
  // A random 0..jitterMs is added to the settle delay so clients that all
  // reconnect together (e.g. a server rollout) don't run the task in lockstep.
  jitterMs?: number;
  // After a success, further reconnects wait out this interval and then share
  // a single trailing run.
  throttleMs?: number;
  // A failed run is retried once after this delay.
  retryDelayMs?: number;
  sentryTag: string;
}

export const DEFAULT_SETTLE_MS = 5_000;
export const DEFAULT_JITTER_MS = 15_000;
const DEFAULT_THROTTLE_MS = 30_000;
const DEFAULT_RETRY_DELAY_MS = 2_000;

/**
 * Settle delay plus a random 0..jitterMs, spreading a fleet-wide reconnect
 * over a window instead of a single instant.
 */
export const getJitteredSettleMs = (
  settleMs: number = DEFAULT_SETTLE_MS,
  jitterMs: number = DEFAULT_JITTER_MS,
): number => settleMs + Math.random() * jitterMs;

// Duck-typed rather than `instanceof Error`, which is false across realms.
const toError = (reason: unknown): Error => {
  if (
    reason &&
    typeof reason === 'object' &&
    typeof (reason as Error).message === 'string'
  ) {
    return reason as Error;
  }
  return new Error(String(reason));
};

/**
 * Runs a task after the StreamWS connection comes back, to recover whatever the
 * gap missed. Waits for the socket to settle (with random jitter) and confirms it is connected,
 * throttles to one run per interval with a single trailing run, and retries
 * once after a delay before reporting the last error to Sentry.
 *
 * The reconnect callback is registered once per `streamWS`; the latest args are
 * read when a timer fires, so a changed `run` neither drops a pending reconnect
 * nor runs with stale values. Timers are cleared only on unmount.
 */
const useStreamWsReconnectTask = (args: StreamWsReconnectTaskArgs): void => {
  const { streamWS, enabled, callbackName } = args;
  const latestRef = useRef(args);
  latestRef.current = args;

  const lastSuccessAtRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  // A reconnect arrived while a run was in flight or throttled; one trailing
  // run covers it, since a single run recovers everything missed.
  const pendingRef = useRef(false);
  const unmountedRef = useRef(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestRunRef = useRef<() => void>(() => undefined);

  // Resolves to the error of a failed attempt, or null on success.
  const attempt = useCallback(async (): Promise<Error | null> => {
    try {
      await latestRef.current.run();
      return null;
    } catch (error) {
      return toError(error);
    }
  }, []);

  requestRunRef.current = () => {
    const { streamWS: socket, enabled: isEnabled } = latestRef.current;
    if (unmountedRef.current || !isEnabled || !socket?.isConnected()) {
      pendingRef.current = false;
      return;
    }
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    const { throttleMs = DEFAULT_THROTTLE_MS } = latestRef.current;
    const lastSuccessAt = lastSuccessAtRef.current;
    const throttleLeftMs =
      lastSuccessAt == null ? 0 : throttleMs - (Date.now() - lastSuccessAt);
    if (throttleLeftMs > 0) {
      pendingRef.current = true;
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          throttleTimerRef.current = null;
          if (pendingRef.current) {
            requestRunRef.current();
          }
        }, throttleLeftMs);
      }
      return;
    }

    pendingRef.current = false;
    inFlightRef.current = true;
    (async () => {
      try {
        let lastError = await attempt();
        if (lastError) {
          await new Promise<void>(resolve => {
            retryTimerRef.current = setTimeout(
              resolve,
              latestRef.current.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS,
            );
          });
          retryTimerRef.current = null;
          if (unmountedRef.current) {
            return;
          }
          lastError = await attempt();
        }
        if (!lastError) {
          lastSuccessAtRef.current = Date.now();
        } else {
          captureExceptionWithScope(
            lastError,
            { status: (lastError as { status?: unknown }).status },
            undefined,
            { feature: latestRef.current.sentryTag },
          );
        }
      } finally {
        inFlightRef.current = false;
      }
      if (pendingRef.current) {
        requestRunRef.current();
      }
    })();
  };

  const handleReconnect = useCallback(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
    }
    settleTimerRef.current = setTimeout(
      () => {
        settleTimerRef.current = null;
        requestRunRef.current();
      },
      getJitteredSettleMs(
        latestRef.current.settleMs,
        latestRef.current.jitterMs,
      ),
    );
  }, []);

  useEffect(() => {
    if (!streamWS || !enabled) {
      return;
    }
    streamWS.addOnReconnectCallback({ [callbackName]: handleReconnect });
    return () => {
      streamWS.removeOnReconnectCallback(callbackName);
    };
  }, [streamWS, enabled, callbackName, handleReconnect]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      pendingRef.current = false;
      [settleTimerRef, throttleTimerRef, retryTimerRef].forEach(timerRef => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      });
    };
  }, []);
};

export default useStreamWsReconnectTask;
